import React, { useState, useEffect } from "react";
import {
  RefreshCw,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import { PrimaryButton } from "../common/ActionButtons";

const MyIssues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [githubUsername, setGithubUsername] = useState("");
  const [githubToken, setGithubToken] = useState("");

  useEffect(() => {
    if (githubUsername && githubToken) {
      fetchIssues();
    }
  }, []);

  const fetchIssues = async () => {
    if (!githubUsername || !githubToken) {
      alert("Please enter your GitHub username and personal access token");
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement GitHub API call
      // const response = await fetch('https://api.github.com/issues?filter=assigned&state=open', {
      //   headers: {
      //     'Authorization': `token ${githubToken}`,
      //     'Accept': 'application/vnd.github.v3+json'
      //   }
      // });
      // const data = await response.json();
      // setIssues(data);

      console.log("Fetch GitHub issues - implement GitHub API");
    } catch (error) {
      console.error("Error fetching issues:", error);
      alert("Failed to fetch issues. Check your token and username.");
    }
    setLoading(false);
  };

  const filteredIssues = issues.filter(
    (issue) =>
      issue.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.repository_url?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getStateColor = (state) => {
    switch (state) {
      case "open":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const getPriorityColor = (labels) => {
    if (!labels) return null;

    const hasCritical = labels.some((l) =>
      l.name.toLowerCase().includes("critical"),
    );
    const hasHigh = labels.some((l) => l.name.toLowerCase().includes("high"));
    const hasBug = labels.some((l) => l.name.toLowerCase().includes("bug"));

    if (hasCritical) return "bg-red-100 text-red-800 border-red-200";
    if (hasHigh) return "bg-orange-100 text-orange-800 border-orange-200";
    if (hasBug) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-blue-100 text-blue-800 border-blue-200";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            My GitHub Issues
          </h1>
          <p className="text-slate-600">
            Track issues assigned to you across all repositories
          </p>
        </div>
        <PrimaryButton
          onClick={fetchIssues}
          icon={RefreshCw}
          disabled={loading || !githubUsername || !githubToken}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>

      {/* GitHub Configuration */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          GitHub Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              GitHub Username *
            </label>
            <input
              type="text"
              value={githubUsername}
              onChange={(e) => setGithubUsername(e.target.value)}
              placeholder="your-username"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Personal Access Token *
            </label>
            <input
              type="password"
              value={githubToken}
              onChange={(e) => setGithubToken(e.target.value)}
              placeholder="ghp_..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Create a token with 'repo' scope at:
          <a
            href="https://github.com/settings/tokens"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline ml-1"
          >
            github.com/settings/tokens
          </a>
        </p>
      </div>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onRefresh={fetchIssues}
        placeholder="Search issues..."
      />

      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              Assigned Issues ({filteredIssues.length})
            </h3>
            {issues.length > 0 && (
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  Open: {issues.filter((i) => i.state === "open").length}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  Closed: {issues.filter((i) => i.state === "closed").length}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="divide-y divide-slate-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <p className="text-slate-600 mt-4">Loading issues...</p>
            </div>
          ) : !githubUsername || !githubToken ? (
            <div className="p-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                Configuration Required
              </h3>
              <p className="text-slate-600">
                Enter your GitHub username and token above to view your issues
              </p>
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                No Issues Found
              </h3>
              <p className="text-slate-600">
                {searchTerm
                  ? "No issues match your search"
                  : "You have no assigned issues. Great job! 🎉"}
              </p>
            </div>
          ) : (
            filteredIssues.map((issue) => {
              const repoName =
                issue.repository_url?.split("/").slice(-2).join("/") ||
                "Unknown";
              const priorityColor = getPriorityColor(issue.labels);

              return (
                <div
                  key={issue.id}
                  className={`p-4 hover:bg-slate-50 transition-colors border-l-4 ${priorityColor}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getStateColor(issue.state)}`}
                        >
                          {issue.state}
                        </span>
                        <span className="text-xs text-slate-500">
                          {repoName}
                        </span>
                        {issue.labels?.map((label) => (
                          <span
                            key={label.id}
                            className="px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor: `#${label.color}20`,
                              color: `#${label.color}`,
                            }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                      <h4 className="text-sm font-medium text-slate-900 mb-1">
                        #{issue.number} {issue.title}
                      </h4>
                      <div className="flex items-center gap-4 text-xs text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created{" "}
                          {new Date(issue.created_at).toLocaleDateString()}
                        </span>
                        {issue.assignees?.length > 0 && (
                          <span>Assignees: {issue.assignees.length}</span>
                        )}
                        {issue.comments > 0 && <span>💬 {issue.comments}</span>}
                      </div>
                    </div>
                    <a
                      href={issue.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Open in GitHub"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-red-900 mb-1">
            🔴 Critical Priority
          </h3>
          <p className="text-sm text-red-800">
            {
              issues.filter((i) =>
                i.labels?.some((l) =>
                  l.name.toLowerCase().includes("critical"),
                ),
              ).length
            }{" "}
            issues
          </p>
        </div>

        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-orange-900 mb-1">
            🟠 High Priority
          </h3>
          <p className="text-sm text-orange-800">
            {
              issues.filter((i) =>
                i.labels?.some((l) => l.name.toLowerCase().includes("high")),
              ).length
            }{" "}
            issues
          </p>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-yellow-900 mb-1">
            🐛 Bug Reports
          </h3>
          <p className="text-sm text-yellow-800">
            {
              issues.filter((i) =>
                i.labels?.some((l) => l.name.toLowerCase().includes("bug")),
              ).length
            }{" "}
            issues
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyIssues;
